import { useState, useRef, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import {
  ModalActionButton,
  ModalCancelButton,
  ModalContent,
  ModalContentActions,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  ModalOverlay,
  ModalPortal,
  ModalRoot,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import { UserGeneratedContentResponse } from '../../../models/user-generated-contents/types';
import { useSolarToast } from '../../../components/solar-toast';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { advocateSelectors } from '../../../models/advocate';
import useScreenTitle from '../../../common/use-screen-title';

type UserGeneratedContentFormProps = {
  children: (props: { onSave: () => void; onSubmit: () => void }) => ReactNode;
};

const UserGeneratedContentForm = ({
  children,
}: UserGeneratedContentFormProps) => {
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const confirmedNavigation = useRef(false);
  const {
    userGeneratedContent,
    upsertUserGeneratedContent,
    submitUserGeneratedContent,
    hasChanges,
  } = useUserGeneratedContent();
  useScreenTitle(userGeneratedContent.title || 'New Post');
  const { showToast } = useSolarToast();
  const advocateId = usePatronSelector(advocateSelectors.getAdvocateId);
  const { t } = useTranslation();
  const history = useHistory();

  // "View posts" targets the member's own submitted-posts list. Prefer the
  // profile route when we know the advocate id; fall back to /account/submitted
  // (the current user's own profile) otherwise. See FE-31691.
  const viewPostsPath = advocateId
    ? `/profiles/${advocateId}/submitted`
    : '/account/submitted';

  const handleSaveSuccess = () => {
    confirmedNavigation.current = true;
    history.goBack();

    showToast({
      title: t('user_generated_content.toast.saved_as_draft'),
      variant: 'success',
      iconName: 'save',
      action: {
        label: t('user_generated_content.toast.view_posts'),
        onClick: () => history.push(viewPostsPath),
      },
    });
  };

  const handleSubmitSuccess = (response?: UserGeneratedContentResponse) => {
    confirmedNavigation.current = true;
    history.goBack();

    // The server is the source of truth for the approval outcome: a topic that
    // auto-publishes returns 'published'; one that needs approval returns
    // 'for_review'/'submitted'. Drive the toast off that rather than
    // re-deriving approval rules on the client.
    const publicationState = response?.data?.attributes?.publication_state;
    const title =
      publicationState === 'published'
        ? t('user_generated_content.toast.published')
        : t('user_generated_content.toast.submitted_for_review');

    showToast({ title, variant: 'success', iconName: 'check' });
  };

  const handleSubmitFailure = (err: Error) => {
    console.error('UGC draft error', err);

    showToast({
      title: t('errors.default'),
      variant: 'error',
      iconName: 'error',
    });
  };

  const handleSave = () => {
    upsertUserGeneratedContent({
      onSuccess: handleSaveSuccess,
      onError: handleSubmitFailure,
      publicationState: 'draft',
    });
  };

  const handleSubmit = () => {
    submitUserGeneratedContent({
      onSuccess: handleSubmitSuccess,
      onError: handleSubmitFailure,
    });
  };

  const handleDiscardChanges = () => {
    if (confirmedNavigation.current) return true;
    setShowDiscardDialog(true);
    return false;
  };

  useEffect(() => {
    if (!hasChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = t('user_generated_content.discard_dialog.message');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges, t]);

  return (
    <>
      <Prompt
        message={handleDiscardChanges}
        when={hasChanges && !confirmedNavigation.current}
      />

      {showDiscardDialog && (
        <ModalRoot
          defaultOpen={true}
          onOpenChange={() => setShowDiscardDialog(false)}
          mode="dialog"
        >
          <ModalPortal>
            <ModalOverlay />
            <ModalContent size="small">
              <ModalContentHeader
                title={t('user_generated_content.discard_dialog.title')}
              />
              <ModalContentBody>
                {t('user_generated_content.discard_dialog.message')}
              </ModalContentBody>
              <ModalContentFooter>
                <ModalContentActions>
                  <ModalCancelButton
                    label={t('common.back')}
                    onClick={() => {
                      setShowDiscardDialog(false);
                    }}
                  />
                  <ModalActionButton
                    variant="danger"
                    label={t('common.discard')}
                    onClick={() => {
                      setShowDiscardDialog(false);
                      confirmedNavigation.current = true;
                      history.goBack();
                    }}
                  />
                </ModalContentActions>
              </ModalContentFooter>
            </ModalContent>
          </ModalPortal>
        </ModalRoot>
      )}

      <form
        aria-label={t('user_generated_content.form_label')}
        onSubmit={(e) => e.preventDefault()}
      >
        {children({
          onSave: handleSave,
          onSubmit: handleSubmit,
        })}
      </form>
    </>
  );
};

export default UserGeneratedContentForm;
